import { useEffect, useState } from "preact/hooks";
import { del, get, html, IN_APP, navigate, patch, post, put } from "../lib.js";
import { Badge, Field, Loading, PageHeader, toast, useAction, useForm, useLoad } from "../ui.js";
import { locale, tr } from "../i18n.js";
import { Billing } from "./billing.js";
import { PageGuide } from "../guide.js";
import { InviteOutcome } from "./admin.js";
import { ApiKeys } from "./api-keys.js";
import { alertFix, currentProblems, KIND_LABEL, STATUS_LABEL } from "../alert-reasons.js";

const TABS = [
  { key: "business", label: "Business" },
  { key: "team", label: "Team" },
  { key: "alerts", label: "Team alerts" },
  { key: "integrations", label: "Integrations" },
  // Never in the Android app (IN_APP in lib.js).
  { key: "billing", label: "Billing", roles: ["owner", "admin"], web: true },
  { key: "account", label: "Account" },
];

/** The zones our customers are in today; the full list follows them in the picker. */
const COMMON_ZONES = ["Asia/Jerusalem", "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Athens", "Asia/Nicosia", "Asia/Dubai", "America/New_York", "America/Chicago", "America/Los_Angeles", "America/Toronto", "Australia/Sydney"];

/** "Asia/Jerusalem (GMT+3)": the offset today, so the owner can recognise their zone without knowing its IANA name. */
function zoneLabel(zone) {
  try {
    const offset = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" }).formatToParts(new Date()).find((p) => p.type === "timeZoneName")?.value;
    return `${zone.replace(/_/g, " ")}${offset ? ` (${offset})` : ""}`;
  } catch {
    return zone;
  }
}

function TimeZoneSelect({ value, ...rest }) {
  // supportedValuesOf is missing on older Safari; the common zones and the current one still show.
  const all = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  const common = COMMON_ZONES.filter((z) => z === value || all.length === 0 || all.includes(z));
  const others = all.filter((z) => !COMMON_ZONES.includes(z));
  if (value && !common.includes(value) && !others.includes(value)) others.unshift(value);
  return html`<select required dir="ltr" value=${value} ...${rest}>
    <optgroup label=${tr("Common")}>${common.map((z) => html`<option value=${z}>${zoneLabel(z)}</option>`)}</optgroup>
    ${others.length > 0 && html`<optgroup label=${tr("All time zones")}>${others.map((z) => html`<option value=${z}>${zoneLabel(z)}</option>`)}</optgroup>`}
  </select>`;
}

export function SettingsPage({ t }) {
  const tab = location.pathname.split("/")[2] || "business";
  useEffect(() => {
    // Where the Google or Microsoft sign-in sends the owner back to.
    const params = new URLSearchParams(location.search);
    const google = params.get("google");
    const microsoft = params.get("microsoft");
    const zoom = params.get("zoom");
    if (zoom === "connected") toast(tr("Zoom connected"));
    if (zoom === "error") toast(tr("Zoom connection failed"), "error");
    if (google === "connected") toast(tr("Google Calendar connected"));
    if (google === "error") toast(tr("Google Calendar connection failed"), "error");
    if (microsoft === "connected") toast(tr("Outlook calendar connected"));
    if (microsoft === "error") toast(tr("Outlook calendar connection failed"), "error");
    if (google || microsoft || zoom) history.replaceState(null, "", location.pathname);
  }, []);
  return html`<div>
    <${PageHeader} title=${tr("Settings")} />
    <div class="tabs">${TABS.filter((x) => (!x.roles || x.roles.includes(t.role)) && !(x.web && IN_APP)).map((x) => html`<button class=${tab === x.key ? "active" : ""} onClick=${() => navigate(`/settings/${x.key}`)}>${tr(x.label)}</button>`)}</div>
    <${PageGuide} id=${`settings.${tab}`} />
    ${tab === "business" && html`<${Business} t=${t} />`}
    ${tab === "team" && html`<${Team} t=${t} />`}
    ${tab === "alerts" && html`<${Alerts} t=${t} />`}
    ${tab === "integrations" && html`<${Integrations} t=${t} />`}
    ${tab === "billing" && (IN_APP ? html`<p class="muted">${tr("Billing isn't available in the app.")}</p>` : html`<${Billing} t=${t} />`)}
    ${tab === "account" && html`<${Account} />`}
  </div>`;
}

function Business({ t }) {
  const s = t.settings;
  const [holidayData] = useLoad(() => get(`${t.base}/holidays`), [t.id]);
  const [holidaysClosed, setHolidaysClosed] = useState(s.holidays_closed !== false);
  const [openOn, setOpenOn] = useState(new Set(s.holiday_exceptions ?? []));
  const { values, bind } = useForm({
    name: t.name,
    timezone: t.timezone,
    address: s.address ?? "",
    contact_email: s.contact_email ?? "",
    website_url: s.website_url ?? "",
    meeting_url: s.meeting_url ?? "",
    min_notice_min: s.min_notice_min,
    max_days_ahead: s.max_days_ahead,
    slot_step_min: s.slot_step_min,
    closed_dates: (s.closed_dates ?? []).join("\n"),
  });
  const [busy, run] = useAction();
  const submit = async (e) => {
    e.preventDefault();
    await run(
      () =>
        patch(t.base, {
          name: values.name,
          timezone: values.timezone,
          settings: {
            address: values.address,
            contact_email: values.contact_email.trim(),
            website_url: values.website_url.trim(),
            meeting_url: values.meeting_url.trim(),
            min_notice_min: Number(values.min_notice_min),
            max_days_ahead: Number(values.max_days_ahead),
            slot_step_min: Number(values.slot_step_min),
            closed_dates: values.closed_dates.split(/[\s,]+/).filter(Boolean),
            holidays_closed: holidaysClosed,
            holiday_exceptions: [...openOn],
          },
        }),
      tr("Settings saved"),
    );
    t.reload();
  };
  return html`<form class="card" onSubmit=${submit}>
    <div class="row">
      <${Field} label=${tr("Business name")}><input required ...${bind("name")} /></${Field}>
      <${Field} label=${tr("Time zone")}><${TimeZoneSelect} ...${bind("timezone")} /></${Field}>
    </div>
    <${Field} label=${tr("Address and contact details")} hint=${tr("Shared by the agent when customers ask how to get there.")}><textarea rows="3" dir="auto" ...${bind("address")}></textarea></${Field}>
    <div class="row">
      <${Field} label=${tr("Customer contact email")} hint=${tr("Shared by the agent, and replies to emails it sends customers arrive here.")}><input type="email" dir="ltr" placeholder="hello@example.com" ...${bind("contact_email")} /></${Field}>
      <${Field} label=${tr("Website")} hint=${tr("The agent may share it, and reads it under AI agent → Documents & links.")}><input type="url" dir="ltr" placeholder="https://" ...${bind("website_url")} /></${Field}>
      <${Field} label=${tr("Default meeting room")} hint=${tr("Your Zoom, Teams or Meet link, used for online services when a service or staff member has none of their own.")}><input type="url" dir="ltr" placeholder="https://zoom.us/j/…" ...${bind("meeting_url")} /></${Field}>
    </div>
    <div class="row">
      <${Field} label=${tr("Minimum notice (minutes)")}><input type="number" min="0" ...${bind("min_notice_min")} /></${Field}>
      <${Field} label=${tr("Book up to (days ahead)")}><input type="number" min="1" max="365" ...${bind("max_days_ahead")} /></${Field}>
      <${Field} label=${tr("Offer start times every (min)")}><input type="number" min="5" max="240" ...${bind("slot_step_min")} /></${Field}>
    </div>
    ${holidayData?.built_in && html`<div class="field">
      <label class="toggle"><input type="checkbox" checked=${holidaysClosed} onChange=${(e) => setHolidaysClosed(e.target.checked)} /> ${tr("Closed on Israeli public holidays")}</label>
      <p class="hint">${tr("Filled in automatically every year. Untick a holiday you open on.")}</p>
      ${holidaysClosed && html`<ul class="plain holiday-list">${holidayData.holidays.map((h) => html`<li class="small">
        <label class="toggle"><input type="checkbox" checked=${!openOn.has(h.date)} onChange=${(e) => {
          const next = new Set(openOn);
          if (e.target.checked) next.delete(h.date);
          else next.add(h.date);
          setOpenOn(next);
        }} /> ${h.date} · ${locale() === "he" ? h.name_he : h.name}</label>
      </li>`)}</ul>`}
    </div>`}
    <${Field} label=${tr("Closed dates")} hint=${tr("One per line, YYYY-MM-DD (vacations, one-off closures, holiday eves).")}><textarea rows="4" ...${bind("closed_dates")}></textarea></${Field}>
    ${t.ai_budget?.cap != null && (t.ai_budget.source === "custom" || t.ai_budget.spent >= t.ai_budget.cap * 0.8) && html`<div class=${`callout small ${t.ai_budget.reached ? "warn" : ""}`}>
      ${tr("AI budget this month: ${spent} of ${cap}.", { spent: t.ai_budget.spent.toFixed(2), cap: t.ai_budget.cap })}
      ${t.ai_budget.reached ? ` ${tr("Used up: new messages go to “Needs a person” until it is raised or the month ends.")}` : ""} ${tr("Ask your RELAYZA contact to change it.")}
    </div>`}
    <div class="form-actions"><button class="btn primary" disabled=${busy}>${tr("Save")}</button></div>
  </form>`;
}

const ROLE_LABEL = { owner: "Owner", admin: "Admin", agent: "Agent" };

function Team({ t }) {
  const [data, reload] = useLoad(() => get(`${t.base}/members`), [t.id]);
  const { values, bind, setValues } = useForm({ name: "", email: "", role: "agent" });
  const [created, setCreated] = useState(null);
  const [busy, run] = useAction();
  const add = async (e) => {
    e.preventDefault();
    const res = await run(() => post(`${t.base}/members`, values), tr("Team member added"));
    setCreated(res.invite ?? null);
    setValues({ name: "", email: "", role: "agent" });
    reload();
  };
  return html`<div class="grid-2">
    <section class="card">
      <h2>${tr("Team")}</h2>
      ${!data && html`<${Loading} />`}
      <table class="table"><tbody>${data?.members.map((m) => html`<tr>
        <td><strong>${m.name}</strong><div class="muted small">${m.email}</div></td>
        <td><${Badge} kind="neutral">${tr(ROLE_LABEL[m.role] ?? m.role)}</${Badge}>${m.invited_at && html` <${Badge} kind="warn">${tr("Invite pending")}</${Badge}>`}</td>
        <td class="row-actions">
          ${m.invited_at && html`<button class="btn small ghost" onClick=${async () => { const res = await run(() => post(`${t.base}/members/${m.id}/invite`)); setCreated(res.invite); }}>${tr("Resend invite")}</button>`}
          ${m.role !== "owner" && html`<button class="btn small ghost" onClick=${async () => { await run(() => del(`${t.base}/members/${m.id}`), tr("Removed")); reload(); }}>${tr("Remove")}</button>`}</td>
      </tr>`)}</tbody></table>
    </section>
    <form class="card" onSubmit=${add}>
      <h2>${tr("Add a team member")}</h2>
      <${Field} label=${tr("Name")}><input required ...${bind("name")} /></${Field}>
      <${Field} label=${tr("Email")}><input type="email" required ...${bind("email")} /></${Field}>
      <p class="muted small">${tr("We email them a link to choose their own password.")}</p>
      <${Field} label=${tr("Role")} hint=${tr("Agents work the inbox and bookings; admins also change settings.")}><select ...${bind("role")}><option value="agent">${tr("Agent")}</option><option value="admin">${tr("Admin")}</option></select></${Field}>
      <button class="btn primary" disabled=${busy}>${tr("Add")}</button>
      ${created && html`<${InviteOutcome} invite=${created} />`}
    </form>
  </div>`;
}

/** Renders a translated sentence whose {code} placeholder becomes a <code> element. */
function withCode(text, code) {
  const [before, after = ""] = text.split("{code}");
  return html`${before}<code>${code}</code>${after}`;
}

const BRIEF_HOURS = Array.from({ length: 16 }, (_, i) => i + 5);

function Alerts({ t }) {
  const [cfg, reloadCfg] = useLoad(() => get(`${t.base}/agent`), [t.id]);
  const [log, reloadLog] = useLoad(() => get(`${t.base}/alerts`), [t.id]);
  const [preview] = useLoad(() => get(`${t.base}/brief`), [t.id]);
  if (!cfg) return html`<${Loading} />`;
  const problems = currentProblems(log?.alerts);
  return html`<div class="grid-2">
    <${AlertForm} t=${t} cfg=${cfg} onSaved=${() => { reloadCfg(); reloadLog(); }} />
    <div>
      <section class="card">
        <h2>${tr("Today's brief, as it would look now")}</h2>
        ${preview ? html`<pre class="code brief-text" dir="auto">${preview.text}</pre>` : html`<${Loading} />`}
      </section>
      <section class="card">
        <div class="section-head"><h2>${tr("Recent alerts")}</h2><button class="btn small ghost" onClick=${reloadLog}>${tr("Refresh")}</button></div>
        ${problems.length > 0 && html`<div class="callout warn small">
          <strong>${tr("The last alert didn't reach everyone:")}</strong>
          <ul class="plain">${problems.map((p) => html`<li><${AlertFix} error=${p} /></li>`)}</ul>
        </div>`}
        ${!log && html`<${Loading} />`}
        ${log && !log.alerts.length && html`<p class="muted small">${tr("Nothing sent yet.")}</p>`}
        <ul class="plain">${log?.alerts.map((a) => html`<li class="small">
          <${Badge} kind=${a.status === "sent" ? "ok" : a.status === "failed" ? "warn" : "neutral"}>${tr(STATUS_LABEL[a.status] ?? a.status)}</${Badge}>
          <strong> ${tr(KIND_LABEL[a.kind] ?? a.kind)}</strong> <span class="muted"><bdi>${a.recipient ?? ""}</bdi> · ${new Date(a.created_at).toLocaleString(locale())}</span>
          ${a.kind === "customer_email" && a.body && html`<div class="muted" dir="auto">${a.body}</div>`}
          ${a.error && html`<div class="error-text" dir="auto">${tr(a.error)}</div>`}
        </li>`)}</ul>
      </section>
    </div>
  </div>`;
}

/** One reason, translated, with what to do about it and a button that goes there. */
function AlertFix({ error }) {
  const f = alertFix(error);
  if (!f) return html`<span dir="auto">${tr(error)}</span>`;
  const go = () => {
    if (f.action === "channels") return navigate("/channels");
    const el = document.getElementById(f.action === "template" ? "alert-template" : "alert-phones");
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
    el?.focus();
  };
  return html`<span>${tr(f.fix)}</span>${f.action && html` <button type="button" class="btn small ghost" onClick=${go}>${tr(FIX_BUTTON[f.action])}</button>`}`;
}

const FIX_BUTTON = { template: "Set the template", phones: "Add recipients", channels: "Open Channels" };

/** "Sent to 1 of 2. …4642: <fix>" from the test endpoint's per-recipient outcomes. */
function testSummary(r) {
  const total = r.outcomes?.length || r.sent + r.failed + r.skipped;
  const head = r.sent ? tr("Test sent to {n} of {total}.", { n: r.sent, total }) : tr("Test not sent.");
  const why = (r.outcomes ?? [])
    .filter((o) => o.status !== "sent" && o.error)
    .map((o) => `${o.recipient ? `${o.recipient}: ` : ""}${tr(alertFix(o.error)?.fix ?? o.error)}`);
  return [head, ...why].join(" ");
}

function AlertForm({ t, cfg, onSaved }) {
  const { values, bind, check } = useForm({
    alert_phones: cfg.alert_phones.split(",").filter(Boolean).map((p) => `+${p}`).join(", "),
    alert_template: cfg.alert_template,
    alert_emails: (cfg.alert_emails ?? "").split(",").filter(Boolean).join(", "),
    handoff_alerts: cfg.handoff_alerts,
    brief_enabled: cfg.brief_enabled,
    brief_hour: cfg.brief_hour,
  });
  const [busy, run] = useAction();
  const submit = async (e) => {
    e.preventDefault();
    await run(() => patch(`${t.base}/agent`, { ...values, brief_hour: Number(values.brief_hour) }), tr("Alert settings saved"));
    onSaved();
  };
  const test = async () => {
    const r = await run(() => post(`${t.base}/alerts/test`));
    toast(testSummary(r), r.sent && !r.failed && !r.skipped ? "ok" : "error");
    onSaved();
  };
  return html`<form class="card" onSubmit=${submit}>
    <h2>${tr("Alerts to your team")}</h2>
    <p class="muted small">${tr("Sent from your connected WhatsApp business number to the phones below, and by email to the addresses below.")}</p>
    <${Field} label=${tr("Staff phone numbers")} hint=${tr("Up to 3, comma-separated, e.g. +972 50 123 4567. A number starting with 0 is read as Israeli.")}><input id="alert-phones" dir="ltr" ...${bind("alert_phones")} /></${Field}>
    <${Field} label=${tr("Alert template (recommended)")} hint=${tr("The name of an approved WhatsApp Utility template with one variable, {{1}}, between fixed text (e.g. \"RELAYZA alert: {{1}} Details in the dashboard.\"). Without it, alerts only reach a phone that wrote to your business number in the last 24 hours. Sent in the template language set on the AI agent page.")}><input id="alert-template" dir="ltr" ...${bind("alert_template")} /></${Field}>
    <${Field} label=${tr("Staff emails")} hint=${tr("Up to 5, comma-separated. The same alerts also go by email.")}><input dir="ltr" ...${bind("alert_emails")} /></${Field}>
    <label class="toggle"><input type="checkbox" ...${check("handoff_alerts")} /> ${tr("Alert when a chat needs a person")}</label>
    <label class="toggle"><input type="checkbox" ...${check("brief_enabled")} /> ${tr("Send a daily brief on open days")}</label>
    <${Field} label=${tr("Brief time")} hint=${tr("Today's appointments, chats waiting, tasks due and yesterday's results.")}><select ...${bind("brief_hour")}>${BRIEF_HOURS.map((h) => html`<option value=${h}>${String(h).padStart(2, "0")}:00</option>`)}</select></${Field}>
    <div class="form-actions">
      <button type="button" class="btn" disabled=${busy} onClick=${test}>${tr("Send test alert")}</button>
      <button class="btn primary" disabled=${busy}>${tr("Save")}</button>
    </div>
  </form>`;
}

const CALENDAR_NAME = { google: "Google Calendar", microsoft: "Outlook / Microsoft 365" };

/** One calendar account per business, Google or Outlook; connecting one replaces the other. */
function CalendarCard({ calendar, busy, connect, disconnect }) {
  if (!calendar) return html`<section class="card"><h2>${tr("Calendar")}</h2><${Loading} /></section>`;
  const p = calendar.provider;
  const configured = { google: calendar.google_configured, microsoft: calendar.microsoft_configured };
  const account = html`<strong>${tr(CALENDAR_NAME[p] ?? "")}</strong>${calendar.account_email ? ` · ${calendar.account_email}` : ""}`;
  const other = p === "google" ? "microsoft" : "google";
  if (calendar.connected && calendar.needs_reconnect) {
    return html`<section class="card">
      <h2>${tr("Calendar")}</h2>
      <p><${Badge} kind="warn">${tr("Reconnect needed")}</${Badge}> ${account}</p>
      <p class="muted small">${p === "microsoft"
        ? tr("Microsoft stopped accepting this connection, usually because access was revoked or the password changed in the Microsoft account. Until it is reconnected the agent books against its own schedule only, so events added straight to the calendar no longer block slots.")
        : tr("Google stopped accepting this connection, usually because access was revoked in the Google account. Until it is reconnected the agent books against its own schedule only, so events added straight to the calendar no longer block slots.")}</p>
      <button class="btn primary" disabled=${busy} onClick=${() => connect(p)}>${p === "microsoft" ? tr("Reconnect Outlook calendar") : tr("Reconnect Google Calendar")}</button>
      <button class="btn ghost" disabled=${busy} onClick=${disconnect}>${tr("Disconnect")}</button>
    </section>`;
  }
  if (calendar.connected) {
    return html`<section class="card">
      <h2>${tr("Calendar")}</h2>
      <p><${Badge} kind="ok">${tr("Connected")}</${Badge}> ${account}</p>
      <p class="muted small">${tr("Pick which calendar each staff member uses under Services & staff.")}</p>
      <button class="btn ghost" disabled=${busy} onClick=${disconnect}>${tr("Disconnect")}</button>
      ${configured[other] && html`<p class="muted small">${tr("Switching replaces this connection, and each staff member's calendar is picked again under Services & staff.")}</p>
        <button class="btn" disabled=${busy} onClick=${() => connect(other)}>${tr("Switch to {provider}", { provider: tr(CALENDAR_NAME[other]) })}</button>`}
    </section>`;
  }
  // The "not verified" notice is temporary: remove it once Google approves the RELAYZA OAuth app
  // (Cloud project relayza-511506). Until then Google warns before its consent screen.
  return html`<section class="card">
    <h2>${tr("Calendar")}</h2>
    <p class="muted small">${tr("Let the agent see real availability and add bookings straight to your calendar.")}</p>
    ${configured.google && html`<p class="muted small">${tr("Google may show a \"Google hasn't verified this app\" notice while its review of RELAYZA is in progress. Choose Advanced, then Continue to RELAYZA. We only create, move and delete the events RELAYZA books.")}</p>`}
    <div class="form-actions">
      <button class="btn primary" disabled=${busy || !configured.google} onClick=${() => connect("google")}>${tr("Connect Google Calendar")}</button>
      ${configured.microsoft && html`<button class="btn primary" disabled=${busy} onClick=${() => connect("microsoft")}>${tr("Connect Outlook / Microsoft 365")}</button>`}
    </div>
    ${!configured.google && html`<p class="muted small">${tr("Google OAuth isn't configured on this deployment yet.")}</p>`}
  </section>`;
}

/**
 * Zoom cloud recordings into call notes. Hidden entirely until the platform has a Zoom app
 * (ZOOM_* secrets), so nobody is offered a button that can't work.
 */
function ZoomCard({ t }) {
  const [zoom, reload] = useLoad(() => get(`${t.base}/integrations/zoom`), [t.id]);
  const [busy, run] = useAction();
  if (!zoom?.configured || !zoom.available) return null;
  const connect = async () => {
    const { url } = await run(() => post(`${t.base}/integrations/zoom/start`));
    location.href = url;
  };
  const disconnect = async () => {
    await run(() => del(`${t.base}/integrations/zoom`), tr("Disconnected"));
    reload();
  };
  return html`<section class="card">
    <h2>${tr("Zoom recordings")}</h2>
    <p class="muted small">${tr("Cloud recordings of your Zoom meetings arrive in Call notes on their own, summarized in your template. Needs a Zoom plan with cloud recording.")}</p>
    ${zoom.connected && zoom.needs_reconnect && html`<p><${Badge} kind="warn">${tr("Reconnect needed")}</${Badge}> ${zoom.account_email ?? ""}</p>`}
    ${zoom.connected && !zoom.needs_reconnect && html`<p><${Badge} kind="ok">${tr("Connected")}</${Badge}> ${zoom.account_email ?? ""}</p>`}
    <div class="form-actions">
      ${(!zoom.connected || zoom.needs_reconnect) && html`<button class="btn primary" disabled=${busy} onClick=${connect}>${zoom.connected ? tr("Reconnect Zoom") : tr("Connect Zoom")}</button>`}
      ${zoom.connected && html`<button class="btn ghost" disabled=${busy} onClick=${disconnect}>${tr("Disconnect")}</button>`}
    </div>
  </section>`;
}

function Integrations({ t }) {
  const [calendar, reloadCalendar] = useLoad(() => get(`${t.base}/integrations/calendar`), [t.id]);
  const [hooks, reloadHooks] = useLoad(() => get(`${t.base}/webhooks`), [t.id]);
  const { values, bind, setValues } = useForm({ url: "", events: "*" });
  const [secret, setSecret] = useState(null);
  const [busy, run] = useAction();

  const connect = async (provider) => {
    const { url } = await run(() => post(`${t.base}/integrations/${provider}/start`));
    location.href = url;
  };
  const disconnect = async () => {
    await run(() => del(`${t.base}/integrations/calendar`), tr("Disconnected"));
    reloadCalendar();
  };
  const addHook = async (e) => {
    e.preventDefault();
    const res = await run(() => post(`${t.base}/webhooks`, values), tr("Webhook added"));
    setSecret(res.secret);
    setValues({ url: "", events: "*" });
    reloadHooks();
  };

  return html`<div class="grid-2">
    <${CalendarCard} calendar=${calendar} busy=${busy} connect=${connect} disconnect=${disconnect} />
    <${ZoomCard} t=${t} />
    <section class="card">
      <h2>${tr("Webhooks")}</h2>
      <p class="muted small">${withCode(tr("Send events (booking.created, conversation.handoff, contact.stage_changed…) to your CRM, Zapier or Make. Each request is signed with HMAC-SHA256 in the {code} header."), "x-signature")}</p>
      <ul class="plain">${hooks?.webhooks.map((h) => html`<li class="section-head small"><span>${h.url} <span class="muted">(${h.events})</span></span>
        <button class="btn small ghost" onClick=${async () => { await run(() => del(`${t.base}/webhooks/${h.id}`)); reloadHooks(); }}>${tr("Remove")}</button></li>`)}</ul>
      <form onSubmit=${addHook}>
        <${Field} label=${tr("URL")}><input type="url" required placeholder="https://" ...${bind("url")} /></${Field}>
        <${Field} label=${tr("Events")} hint=${tr("* for all, or a comma-separated list")}><input ...${bind("events")} /></${Field}>
        <button class="btn" disabled=${busy}>${tr("Add webhook")}</button>
      </form>
      ${secret && html`<div class="callout ok small">${tr("Signing secret (shown once):")} <code>${secret}</code></div>`}
    </section>
    <${Crm} t=${t} />
    <${Payments} t=${t} />
    <${ApiKeys} t=${t} />
  </div>`;
}

// Same shape as GROW_ID in src/payments/grow.ts: Grow's ids are hex, 16 (userId) and 12 (pageCode) long today.
const GROW_ID = /^[0-9a-f]{6,32}$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Why a pasted value can't be a Grow id, or null when it can. */
function growIdProblem(value, field) {
  const v = value.trim();
  if (!v || GROW_ID.test(v)) return null;
  if (UUID.test(v)) return tr("This looks like an API key from another service, such as Morning, not a code from Grow.");
  return field === "user_id" ? tr("This doesn't look like a Grow userId.") : tr("This doesn't look like a Grow pageCode.");
}

// Same shape as MAKE_HOOK in src/payments/grow.ts.
const MAKE_HOOK = /^https:\/\/hook\.(?:[a-z0-9-]+\.)?make\.com\/[A-Za-z0-9_-]{16,}$/;

function Payments({ t }) {
  const [data, reload] = useLoad(() => get(`${t.base}/payments`), [t.id]);
  const { values, bind } = useForm({ user_id: "", page_code: "", create_url: "" });
  const [tried, setTried] = useState(false);
  // Grow opens its API for some accounts and points others to its app for Make.
  const [via, setVia] = useState("api");
  const [testLink, setTestLink] = useState(null);
  const [busy, run] = useAction();
  if (!data) return html`<section class="card"><${Loading} /></section>`;
  const isOwner = t.role === "owner";
  // Errors show once the owner has tried to save, then follow the input as they fix it.
  const hookProblem = values.create_url.trim() && !MAKE_HOOK.test(values.create_url.trim()) ? tr("This isn't a Make webhook address. Copy it from the Custom webhook module: https://hook.….make.com/…") : null;
  const problems = { user_id: growIdProblem(values.user_id, "user_id"), page_code: growIdProblem(values.page_code, "page_code"), create_url: hookProblem };
  const fieldError = (key) => tried && problems[key] && html`<span class="error-text small" role="alert">${problems[key]}</span>`;
  const connect = async (e) => {
    e.preventDefault();
    setTried(true);
    if (via === "make") {
      if (problems.create_url) return;
      await run(() => put(`${t.base}/payments/grow/make`, { create_url: values.create_url.trim() }), tr("Grow connected through Make"));
    } else {
      if (problems.user_id || problems.page_code) return;
      await run(() => put(`${t.base}/payments/grow`, { user_id: values.user_id.trim(), page_code: values.page_code.trim() }), tr("Grow connected"));
    }
    reload();
  };
  const test = async () => {
    setTestLink(null);
    const r = await run(() => post(`${t.base}/payments/grow/test`), tr("Make answered with a payment link"));
    setTestLink(r.url);
  };
  const grow = data.grow;
  return html`<section class="card">
    <h2>${tr("Deposits (Grow)")}</h2>
    <p class="muted small">${tr("Take a deposit when a customer books, by card, Bit, Apple Pay or Google Pay, so no-shows drop. Set the amount per service under Services & staff. Unpaid holds are released after 30 minutes.")}</p>
    ${data.sandbox && html`<div class="callout warn small">${tr("Test mode: payments go to Grow's sandbox.")}</div>`}
    ${grow.connected
      ? html`<p><${Badge} kind="ok">${tr("Connected")}</${Badge}> <span class="muted small">${grow.mode === "make" ? html`${tr("through Make")} · <span dir="ltr">${grow.hook}</span>` : html`userId ${grow.user_id} · pageCode ${grow.page_code}`}</span></p>
          ${grow.mode === "make" && isOwner && html`<p class="muted small">${tr("Run a test after any change to the scenario: it asks Make for a ₪1 payment link and shows it here. Don't pay it.")}</p>
            <button class="btn ghost small" disabled=${busy} onClick=${test}>${tr("Run a test")}</button> `}
          ${isOwner && html`<button class="btn ghost small" disabled=${busy} onClick=${async () => { await run(() => del(`${t.base}/payments/grow`), tr("Grow disconnected")); reload(); }}>${tr("Disconnect")}</button>`}
          ${testLink && html`<div class="callout ok small">${tr("The scenario works. Test link:")} <a href=${testLink} target="_blank" rel="noopener" dir="ltr">${testLink}</a></div>`}`
      : html`<div class="segmented" role="group" aria-label=${tr("How Grow connects")}>
          <button type="button" class=${via === "api" ? "active" : ""} onClick=${() => setVia("api")}>${tr("Grow API codes")}</button>
          <button type="button" class=${via === "make" ? "active" : ""} onClick=${() => setVia("make")}>${tr("Through Make")}</button>
        </div>
        ${via === "api"
          ? html`<details>
              <summary>${tr("How do I get these?")}</summary>
              <ol class="small">
                <li>${tr("Sign in to your Grow business account. No account yet? Open one first: Grow is the company that actually collects the deposit.")}</li>
                <li>${tr("Ask Grow's support or integrations team to open API access (Light API) for your account, and ask for your userId and pageCode.")}</li>
                <li>${tr("Ask them to turn on card, Bit, Apple Pay and Google Pay on that payment page.")}</li>
                <li>${tr("Paste both codes here and press Connect Grow.")}</li>
              </ol>
              <p class="muted small">${tr("Both codes come from Grow, not from Morning (Green Invoice). A key from Morning's developer tools won't work here.")}</p>
              <p class="muted small">${tr("Grow said to connect through Make instead? Choose Through Make above.")}</p>
            </details>`
          : html`<details open>
              <summary>${tr("How do I set up Make?")}</summary>
              <ol class="small">
                <li>${tr("Open a free account at make.com and create a scenario. Its first module is Webhooks → Custom webhook; copy the address it gives you.")}</li>
                <li>${tr("Add Grow's own app → Create Payment Link, and connect it with your business ID number and the code Grow texts to your phone.")}</li>
                <li>${tr("Fill it from the webhook: Full Name ← full_name, Phone ← phone, Title ← title, one product priced at sum, Notify URL ← notify_url, Success URL ← success_url, Custom Field 1 ← reference. Sending mode: none.")}</li>
                <li>${tr("End with Webhooks → Webhook response, body {\"url\": …, \"process_id\": …, \"process_token\": …} from Grow's answer, set the scenario to run immediately and turn it on.")}</li>
                <li>${tr("Paste the webhook address here, press Connect Grow, then Run a test.")}</li>
              </ol>
              <p class="muted small">${tr("The payment goes from the customer to Grow; Grow tells us directly that it was paid. Make only creates the link, which costs about 3 Make credits per link.")}</p>
            </details>`}
        <form onSubmit=${connect}>
          ${via === "api"
            ? html`<${Field} label=${tr("Grow userId")} hint=${tr("From Grow when you open API access")}><input required dir="ltr" disabled=${!isOwner} aria-invalid=${Boolean(tried && problems.user_id)} ...${bind("user_id")} />${fieldError("user_id")}</${Field}>
                <${Field} label=${tr("Grow pageCode")} hint=${tr("The payment page code Grow gives you for this integration")}><input required dir="ltr" disabled=${!isOwner} aria-invalid=${Boolean(tried && problems.page_code)} ...${bind("page_code")} />${fieldError("page_code")}</${Field}>`
            : html`<${Field} label=${tr("Make webhook address")} hint=${tr("From the Custom webhook module at the start of your scenario")}><input required type="url" dir="ltr" placeholder="https://hook.eu2.make.com/…" disabled=${!isOwner} aria-invalid=${Boolean(tried && problems.create_url)} ...${bind("create_url")} />${fieldError("create_url")}</${Field}>`}
          <button class="btn" disabled=${busy || !isOwner}>${tr("Connect Grow")}</button>
          ${!isOwner && html`<p class="muted small">${tr("Only the business owner can connect payments.")}</p>`}
        </form>`}
  </section>`;
}

function Crm({ t }) {
  const [data, reload] = useLoad(() => get(`${t.base}/integrations/crm`), [t.id]);
  const [busy, run] = useAction();
  const [hubspotToken, setHubspotToken] = useState("");
  const { values, bind } = useForm({ token: "", board_id: "", phone_column: "", email_column: "", status_column: "" });
  const [columns, setColumns] = useState(null);
  if (!data) return html`<section class="card"><${Loading} /></section>`;
  const status = (x) =>
    x.connected
      ? html`<p><${Badge} kind=${x.status === "error" ? "warn" : "ok"}>${x.status === "error" ? tr("Sync error") : tr("Connected")}</${Badge}>
          ${x.last_synced_at && html` <span class="muted small">${tr("last sync {time}", { time: new Date(x.last_synced_at).toLocaleString(locale()) })}</span>`}</p>
          ${x.last_error && html`<div class="callout error small">${x.last_error}</div>`}`
      : null;
  const disconnect = async (provider) => {
    await run(() => del(`${t.base}/integrations/crm/${provider}`), tr("Disconnected"));
    reload();
  };
  const saveMonday = async (e) => {
    e.preventDefault();
    const res = await run(() => put(`${t.base}/integrations/crm/monday`, values), tr("Monday.com connected"));
    setColumns(res.board.columns);
    reload();
  };
  const columnField = (key, label, types) =>
    columns
      ? html`<${Field} label=${label}><select ...${bind(key)}><option value="">${tr("Don't sync")}</option>${columns.filter((c) => types.includes(c.type)).map((c) => html`<option value=${c.id}>${c.title}</option>`)}</select></${Field}>`
      : html`<${Field} label=${label} hint=${tr("Column id; connect once to pick from a list")}><input dir="ltr" ...${bind(key)} /></${Field}>`;
  return html`<section class="card">
    <h2>${tr("CRM sync")}</h2>
    <p class="muted small">${tr("Every lead becomes a CRM record with its stage kept up to date, plus a timeline note for each booking, cancellation and handoff.")}</p>
    <h3>HubSpot</h3>
    ${status(data.hubspot)}
    ${data.hubspot.connected
      ? html`<button class="btn ghost small" disabled=${busy} onClick=${() => disconnect("hubspot")}>${tr("Disconnect HubSpot")}</button>`
      : html`<form onSubmit=${async (e) => { e.preventDefault(); await run(() => put(`${t.base}/integrations/crm/hubspot`, { token: hubspotToken }), tr("HubSpot connected")); setHubspotToken(""); reload(); }}>
          <${Field} label=${tr("Private app access token")} hint=${tr("HubSpot → Settings → Integrations → Private apps, with crm.objects.contacts read and write.")}><input type="password" autocomplete="off" required value=${hubspotToken} onInput=${(e) => setHubspotToken(e.target.value)} /></${Field}>
          <button class="btn" disabled=${busy}>${tr("Connect HubSpot")}</button>
        </form>`}
    <h3>Monday.com</h3>
    ${status(data.monday)}
    ${data.monday.connected && html`<p class="muted small">${tr("Board: {board}", { board: data.monday.config.board_name ?? data.monday.config.board_id })}</p>
      <button class="btn ghost small" disabled=${busy} onClick=${() => disconnect("monday")}>${tr("Disconnect Monday.com")}</button>`}
    <form onSubmit=${saveMonday}>
      <${Field} label=${tr("API token")} hint=${tr("Monday → your avatar → Developers → My access tokens")}><input type="password" autocomplete="off" required ...${bind("token")} /></${Field}>
      <${Field} label=${tr("Board id")} hint=${tr("The number in the board's URL")}><input inputmode="numeric" required ...${bind("board_id")} /></${Field}>
      ${columnField("phone_column", tr("Phone column"), ["phone"])}
      ${columnField("email_column", tr("Email column"), ["email"])}
      ${columnField("status_column", tr("Stage column"), ["status"])}
      <button class="btn" disabled=${busy}>${data.monday.connected ? tr("Update Monday.com") : tr("Connect Monday.com")}</button>
    </form>
  </section>`;
}

function Account() {
  const empty = { current_password: "", new_password: "", confirm: "" };
  const { values, bind, setValues } = useForm(empty);
  const [busy, run] = useAction();
  const mismatch = values.confirm !== "" && values.confirm !== values.new_password;
  const submit = async (e) => {
    e.preventDefault();
    if (mismatch) return;
    await run(
      () => post("/auth/password", { current_password: values.current_password, new_password: values.new_password }),
      tr("Password changed; other devices were signed out"),
    );
    setValues(empty);
  };
  return html`<form class="card" onSubmit=${submit}>
    <h2>${tr("Change password")}</h2>
    <${Field} label=${tr("Current password")} hint=${tr("Use the temporary password you were given if you haven't changed it yet.")}><input type="password" required autocomplete="current-password" ...${bind("current_password")} /></${Field}>
    <div class="row">
      <${Field} label=${tr("New password")} hint=${tr("At least 8 characters.")}><input type="password" required minlength="8" maxlength="200" autocomplete="new-password" ...${bind("new_password")} /></${Field}>
      <${Field} label=${tr("Confirm new password")} hint=${mismatch ? tr("Passwords don't match.") : ""}><input type="password" required autocomplete="new-password" ...${bind("confirm")} /></${Field}>
    </div>
    <div class="form-actions"><button class="btn primary" disabled=${busy || mismatch}>${tr("Change password")}</button></div>
  </form>`;
}
